import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LAST_ROUTE_KEY = 'viabiliza_last_route';
const LAST_SCROLL_KEY = 'viabiliza_last_scroll';

export const useNavigationPersistence = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Save current route and scroll position to localStorage
  useEffect(() => {
    if (location.pathname !== '/') {
      localStorage.setItem(LAST_ROUTE_KEY, location.pathname + location.search + location.hash);
      localStorage.setItem(LAST_SCROLL_KEY, window.scrollY.toString());
    }
  }, [location.pathname, location.search, location.hash]);

  // Save scroll position when user scrolls
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname !== '/') {
        localStorage.setItem(LAST_SCROLL_KEY, window.scrollY.toString());
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Save state when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (location.pathname !== '/') {
        localStorage.setItem(LAST_ROUTE_KEY, location.pathname + location.search + location.hash);
        localStorage.setItem(LAST_SCROLL_KEY, window.scrollY.toString());
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && location.pathname !== '/') {
        localStorage.setItem(LAST_ROUTE_KEY, location.pathname + location.search + location.hash);
        localStorage.setItem(LAST_SCROLL_KEY, window.scrollY.toString());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, location.search, location.hash]);

  // Restore last route and scroll position
  const restoreLastRoute = () => {
    const lastRoute = localStorage.getItem(LAST_ROUTE_KEY);
    const lastScroll = localStorage.getItem(LAST_SCROLL_KEY);
    
    if (lastRoute && lastRoute !== '/') {
      // If we're on the home route or a different route, navigate to the saved route
      if (location.pathname === '/' || location.pathname !== lastRoute.split('?')[0]) {
        navigate(lastRoute, { replace: true });
        // Scroll restoration disabled: every page should start at the top.
      }
    }
  };

  // Auto-restore on app focus/visibility
  useEffect(() => {
    const handleFocus = () => {
      const lastRoute = localStorage.getItem(LAST_ROUTE_KEY);
      if (lastRoute && lastRoute !== '/' && location.pathname === '/') {
        restoreLastRoute();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastRoute = localStorage.getItem(LAST_ROUTE_KEY);
        if (lastRoute && lastRoute !== '/' && location.pathname === '/') {
          restoreLastRoute();
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname]);

  // Clear saved route and scroll (call this on logout)
  const clearSavedRoute = () => {
    localStorage.removeItem(LAST_ROUTE_KEY);
    localStorage.removeItem(LAST_SCROLL_KEY);
  };

  return {
    restoreLastRoute,
    clearSavedRoute
  };
};